import React, { useState } from 'react';
import {
  X,
  MessageSquarePlus,
  Send,
  CheckCircle2,
  RotateCcw,
  Loader2,
  AlertCircle,
  FileSearch,
  Printer,
} from 'lucide-react';
import { Button, ConfirmationDialog } from '@roger/ui-components';
import {
  checkoutOrder,
  checkinOrder,
  revertCheckoutOrder,
  revertCheckinOrder,
} from '../../services/api';
import type { OrderManagerItemResponse } from '../../services/api/types.gen';
import { sendOrderFax } from '../../services/orderFaxes';
import { useToast } from '../../hooks/useToastContext';
import { useStore } from '../../store';
import ReturnedFaxDialog from './ReturnedFaxDialog';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import NotesDialog from './NotesDialog';

type OrderManagerTabId =
  'not-ready' | 'to-be-sent' | 'pending-signature' | 'received';
/**
 * What a bulk action resolves to. The generated client is configured not to
 * throw, so an HTTP failure (a 403 without permission, a 409 on a stale order)
 * arrives as a RESOLVED value carrying `error` rather than as a rejection.
 * Counting only rejections reported those as successes.
 */
type BulkActionResult = { error?: unknown };
type SelectedOrder = Pick<
  OrderManagerItemResponse,
  | 'id'
  | 'clinic_physician_id'
  | 'physician_deleted'
  | 'signature_required'
  | 'document_count'
  | 'physician_delivery_preference'
  | 'fax_signature_pending'
>;

interface OrderManagerActionsProps {
  activeTab: OrderManagerTabId;
  selectedOrders: SelectedOrder[];
  onClearSelection: () => void;
  onActionComplete: () => void;
}

const OrderManagerActions: React.FC<OrderManagerActionsProps> = ({
  activeTab,
  selectedOrders,
  onClearSelection,
  onActionComplete,
}) => {
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmReceivedOpen, setConfirmReceivedOpen] = useState(false);
  const [reviewingOrderId, setReviewingOrderId] = useState<string | null>(null);
  const { error: showError, success: showSuccess } = useToast();
  // An agency without eFax is offered neither half of it: no send, and no
  // returned-page review standing in front of Mark Received.
  const hasFax = useStore(state => state.hasProduct('fax'));
  const selectedIds = selectedOrders.map(order => order.id);
  const count = selectedIds.length;
  const awaitingFax = hasFax
    ? selectedOrders.filter(order => order.fax_signature_pending)
    : [];
  const awaitingFaxAnswer = awaitingFax.length;
  // Reviewed one at a time: the question is about this page, and a bulk yes
  // over pages nobody opened is the thing this feature exists to prevent.
  const firstAwaitingId = awaitingFax[0]?.id ?? null;
  // A physician deleted from the directory counts as missing: checkout refuses it.
  const hasMissingPhysician = selectedOrders.some(
    order =>
      order.signature_required &&
      (!order.clinic_physician_id || order.physician_deleted)
  );
  const hasNoSignatureCompletedOrder =
    activeTab === 'received' &&
    selectedOrders.some(order => !order.signature_required);
  // The signed order is the audit and billing record for a received order, so
  // marking one received without an attachment asks first. It stays a warning:
  // the attachment can still be added after the order is received.
  const missingAttachmentCount =
    activeTab === 'pending-signature'
      ? selectedOrders.filter(order => !order.document_count).length
      : 0;
  const hasMissingAttachment = missingAttachmentCount > 0;
  const missingAttachmentMessage =
    count === 1
      ? 'This order has no signed order attached. Mark it received anyway?'
      : `${missingAttachmentCount} of ${count} selected orders ${
          missingAttachmentCount === 1 ? 'has' : 'have'
        } no signed order attached. Mark them received anyway?`;
  const missingPhysicianMessage = 'Physician required before sending.';
  // Faxing is only offered where the physician asked to be faxed. Anything
  // else is somebody's mail, courier or email arrangement, and Mark as Sent
  // Externally is the honest record of those.
  const faxableOrders = selectedOrders.filter(
    order => order.physician_delivery_preference === 'fax'
  );
  const canFax = hasFax && faxableOrders.length > 0;
  const faxSkippedCount = count - faxableOrders.length;
  const signatureRequiredOnlyMessage =
    'Only signature-required orders can move back to pending signature.';

  if (count === 0) return null;

  const executeBulkAction = async (
    action: (params: {
      path: { order_id: string };
    }) => Promise<BulkActionResult>,
    getFailureMessage: (failedCount: number, total: number) => string,
    // The orders to act on, when that is not simply everything selected. A
    // mixed selection is ordinary, and an action that only applies to some of
    // it should leave the rest alone rather than counting them as failures.
    ids: string[] = selectedIds
  ) => {
    setIsLoading(true);
    const results = await Promise.allSettled(
      ids.map(id => action({ path: { order_id: id } }))
    );
    // A rejection covers a network or thrown failure; a resolved `error` covers
    // the HTTP failures the client hands back instead of throwing.
    const failedCount = results.filter(
      r => r.status === 'rejected' || r.value.error != null
    ).length;
    if (failedCount > 0) {
      showError(getFailureMessage(failedCount, count));
    }
    setIsLoading(false);
    onClearSelection();
    onActionComplete();
  };

  const handleMarkReceived = () => {
    void executeBulkAction(
      checkinOrder,
      (failed, total) =>
        `Failed to mark ${failed} of ${total} orders as received`
    );
  };

  const handleSendToPhysician = () => {
    void executeBulkAction(
      checkoutOrder,
      (failed, total) => `Failed to send ${failed} of ${total} orders`
    );
  };

  /**
   * Fax the orders whose physician takes faxes.
   *
   * Each order is reported on its own rather than as one count, because the
   * reasons differ per order and "3 of 5 failed" tells a scheduler nothing
   * they can act on. An unresolved send is called out separately: it is the
   * one outcome where sending again could fax the physician twice.
   */
  const handleSendByFax = async () => {
    setIsLoading(true);
    const results = await Promise.allSettled(
      faxableOrders.map(order => sendOrderFax(order.id))
    );

    const failures: string[] = [];
    let sent = 0;
    let unresolved = 0;
    results.forEach(result => {
      if (result.status === 'rejected') {
        failures.push(faxFailureReason(result.reason));
      } else if (result.value.outcome === 'unresolved') {
        unresolved += 1;
      } else {
        sent += 1;
      }
    });

    if (sent > 0) {
      showSuccess(`Faxed ${sent} ${sent === 1 ? 'order' : 'orders'}.`);
    }
    if (unresolved > 0) {
      showError(
        `${unresolved} ${unresolved === 1 ? 'fax' : 'faxes'} could not be ` +
          'confirmed. Check the fax log before sending again, or the ' +
          'physician may get two copies.'
      );
    }
    failures.forEach(reason => showError(reason));

    setIsLoading(false);
    onClearSelection();
    onActionComplete();
  };

  return (
    <>
      <div className='fixed bottom-6 left-1/2 z-50 -translate-x-1/2 transform'>
        <div className='flex max-w-[calc(100vw-2rem)] flex-wrap items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg'>
          <span className='text-sm font-medium'>{count} selected</span>
          <Button
            variant='ghost'
            size='sm'
            onClick={onClearSelection}
            disabled={isLoading}
          >
            <X className='h-4 w-4' />
          </Button>

          <div className='mx-1 h-6 w-px bg-border' />

          <Button
            variant='outline'
            size='sm'
            onClick={() => setNotesDialogOpen(true)}
            disabled={isLoading}
          >
            <MessageSquarePlus className='mr-1.5 h-4 w-4' />
            Notes
          </Button>

          {activeTab === 'to-be-sent' && (
            <>
              {hasMissingPhysician && (
                <span className='flex items-center gap-1 text-xs font-medium text-destructive'>
                  <AlertCircle className='h-3.5 w-3.5' />
                  Physician required
                </span>
              )}
              <Button
                variant='outline'
                size='sm'
                onClick={handleSendToPhysician}
                disabled={isLoading || hasMissingPhysician}
                title={
                  hasMissingPhysician ? missingPhysicianMessage : undefined
                }
              >
                {isLoading ? (
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                ) : (
                  <Printer className='mr-1.5 h-4 w-4' />
                )}
                Mark as Sent Externally
              </Button>
              {canFax && (
                <Button
                  size='sm'
                  onClick={() => void handleSendByFax()}
                  disabled={isLoading || hasMissingPhysician}
                  title={
                    faxSkippedCount > 0
                      ? `${faxSkippedCount} of ${count} selected orders go to a physician who does not take faxes, and will be left alone.`
                      : undefined
                  }
                >
                  {isLoading ? (
                    <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                  ) : (
                    <Send className='mr-1.5 h-4 w-4' />
                  )}
                  {faxSkippedCount > 0
                    ? `Send ${faxableOrders.length} by Fax`
                    : 'Send by Fax'}
                </Button>
              )}
            </>
          )}

          {activeTab === 'pending-signature' && (
            <>
              <Button
                variant='outline'
                size='sm'
                onClick={() =>
                  executeBulkAction(
                    revertCheckoutOrder,
                    (failed, total) =>
                      `Failed to move ${failed} of ${total} orders to To Be Sent`
                  )
                }
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                ) : (
                  <RotateCcw className='mr-1.5 h-4 w-4' />
                )}
                Move to To Be Sent
              </Button>

              {/* One question at a time. A page came back carrying this
                  order's mark, so the only thing worth asking is whether it
                  is signed, and Mark Received is deliberately not offered
                  beside it: it moves the order without recording an answer,
                  which is the whole thing this exists to stop. Deselect the
                  faxed orders to reach it for the rest. */}
              {awaitingFaxAnswer > 0 ? (
                <>
                  <span className='text-xs font-medium text-muted-foreground'>
                    {awaitingFaxAnswer === count
                      ? 'Fax received'
                      : `${awaitingFaxAnswer} of ${count} have a fax back`}
                  </span>
                  {/* The answer is not asked for here. Nobody can tell whether
                      a page is signed from a row in a list, so the only thing
                      offered is a look at it. */}
                  <Button
                    size='sm'
                    onClick={() => setReviewingOrderId(firstAwaitingId)}
                    disabled={isLoading || !firstAwaitingId}
                  >
                    <FileSearch className='mr-1.5 h-4 w-4' />
                    {awaitingFaxAnswer === 1
                      ? 'View returned page'
                      : 'Review returned pages'}
                  </Button>
                </>
              ) : (
                <>
                  {hasMissingAttachment && (
                    <span className='flex items-center gap-1 text-xs font-medium text-destructive'>
                      <AlertCircle className='h-3.5 w-3.5' />
                      No attachment
                    </span>
                  )}
                  <Button
                    size='sm'
                    onClick={
                      hasMissingAttachment
                        ? () => setConfirmReceivedOpen(true)
                        : handleMarkReceived
                    }
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                    ) : (
                      <CheckCircle2 className='mr-1.5 h-4 w-4' />
                    )}
                    Mark Received
                  </Button>
                </>
              )}
            </>
          )}

          {activeTab === 'received' && (
            <>
              {hasNoSignatureCompletedOrder && (
                <span className='flex items-center gap-1 text-xs font-medium text-destructive'>
                  <AlertCircle className='h-3.5 w-3.5' />
                  Signature required only
                </span>
              )}
              <Button
                variant='outline'
                size='sm'
                onClick={() =>
                  executeBulkAction(
                    revertCheckinOrder,
                    (failed, total) =>
                      `Failed to move ${failed} of ${total} orders to pending signature`
                  )
                }
                disabled={isLoading || hasNoSignatureCompletedOrder}
                title={
                  hasNoSignatureCompletedOrder
                    ? signatureRequiredOnlyMessage
                    : undefined
                }
              >
                {isLoading ? (
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                ) : (
                  <RotateCcw className='mr-1.5 h-4 w-4' />
                )}
                Move to Pending Signature
              </Button>
            </>
          )}
        </div>
      </div>

      <ReturnedFaxDialog
        orderId={reviewingOrderId}
        onClose={() => setReviewingOrderId(null)}
        onAnswered={() => {
          onClearSelection();
          onActionComplete();
        }}
        onError={showError}
      />

      <ConfirmationDialog
        open={confirmReceivedOpen}
        title='No attachment'
        message={missingAttachmentMessage}
        confirmLabel='Mark Received'
        onAccept={() => {
          setConfirmReceivedOpen(false);
          handleMarkReceived();
        }}
        onCancel={() => setConfirmReceivedOpen(false)}
      />

      <NotesDialog
        open={notesDialogOpen}
        onOpenChange={setNotesDialogOpen}
        taskIds={selectedIds}
        mode='bulk'
        onSuccess={() => {
          onClearSelection();
          onActionComplete();
        }}
      />
    </>
  );
};

/**
 * The reason the server gave, or a generic one.
 *
 * The refusals this endpoint returns are written for the person reading them
 * ("This physician has no fax number on file"), so they are worth showing. A
 * 5xx body is not, and getBusinessErrorMessage already draws that line.
 */
function faxFailureReason(error: unknown): string {
  return (
    getBusinessErrorMessage(error) ??
    'Could not fax that order. Please try again.'
  );
}

export default OrderManagerActions;
